/**
 * The Filtered Props from 'useSafeForwardProps'
 * @internal
 */
export type UseSafeForwardReturnProps<T, P> = Omit<
  T,
  keyof P | 'className' | 'classNames' | 'style'
>;

/**
 * Filters props out of a prop list. Per default className, classNames & style are filtered.
 * @internal
 */
export function useSafeForwardProps<T extends object, P extends object>(
  props: T,
  excludedProps?: P,
): UseSafeForwardReturnProps<T, P> {
  const ignoreStyles = ['className', 'classNames', 'style'];
  const ignoreList = excludedProps
    ? [...ignoreStyles, ...Object.keys(excludedProps)]
    : ignoreStyles;

  const safeForwardProps = {} as UseSafeForwardReturnProps<T, P>;
  for (const [name, prop] of Object.entries(props)) {
    if (!ignoreList.includes(name)) {
      const localName = name as keyof typeof safeForwardProps;
      safeForwardProps[localName] = prop;
    }
  }

  return safeForwardProps;
}
